import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  createAnnouncer,
  type Announcer,
  type AnnouncePriority
} from '../a11y/announce.js';
import {
  createRovingTabIndex,
  type RovingTabIndex,
  type RovingOrientation
} from '../a11y/roving-tabindex.js';
import {
  installFocusVisibleClass,
  isFocusVisible,
  FOCUS_VISIBLE_CLASS
} from '../a11y/focus-visible.js';
import { prefersReducedMotion, prefersReducedMotionOs } from '../a11y/motion.js';
import { reportContrast, type ContrastReport } from '../a11y/contrast-check.js';
import { useAppStore, type TextScale, type Density } from '../store/app-store.js';
import {
  typography,
  type TypographyBundle,
  type TypographyRole
} from '../sizes/typography.js';
import {
  spacingRole,
  type SpacingByUseBundle,
  type SpacingUse,
  type SpacingRoleOptions
} from '../sizes/spacing-roles.js';
import { spacingSemantic, type SpacingTier } from '../sizes/spacing.js';
import { DENSITY_FACTORS } from '../sizes/spacing-roles.js';

let sharedAnnouncer: Announcer | null = null;
function getSharedAnnouncer(): Announcer {
  if (!sharedAnnouncer) sharedAnnouncer = createAnnouncer();
  return sharedAnnouncer;
}

export function useAnnouncer(): { announce: (message: string, priority?: AnnouncePriority) => void } {
  useEffect(() => {
    getSharedAnnouncer();
    return () => {
      // intentionally don't destroy; the announcer is process-shared
    };
  }, []);
  return {
    announce: (message, priority) => getSharedAnnouncer().announce(message, priority)
  };
}

export interface UseRovingTabIndexOptions {
  itemCount: number;
  orientation?: RovingOrientation;
  wrap?: boolean;
  initialIndex?: number;
}

export function useRovingTabIndex(opts: UseRovingTabIndexOptions): {
  activeIndex: number;
  setActiveIndex: (i: number) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  getItemProps: RovingTabIndex['getItemProps'];
} {
  const ctrlRef = useRef<RovingTabIndex | null>(null);
  if (!ctrlRef.current) ctrlRef.current = createRovingTabIndex(opts);
  const [, force] = useState(0);
  const ctrl = ctrlRef.current;
  return {
    activeIndex: ctrl.activeIndex,
    setActiveIndex: (i) => { ctrl.setActiveIndex(i); force((n) => n + 1); },
    onKeyDown: (event) => {
      ctrl.onKeyDown(event.nativeEvent as unknown as KeyboardEvent);
      force((n) => n + 1);
    },
    getItemProps: (i) => ctrl.getItemProps(i)
  };
}

export function useFocusVisible(): boolean {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const dispose = installFocusVisibleClass();
    const observer = new MutationObserver(() => {
      setVisible(document.body.classList.contains(FOCUS_VISIBLE_CLASS));
    });
    if (typeof document !== 'undefined') {
      observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      setVisible(isFocusVisible());
    }
    return () => {
      observer.disconnect();
      dispose();
    };
  }, []);
  return visible;
}

export function useReducedMotion(): boolean {
  const storePref = useAppStore((s) => s.reducedMotion);
  const [osPrefers, setOsPrefers] = useState(prefersReducedMotionOs());
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const listener = (e: MediaQueryListEvent) => setOsPrefers(e.matches);
    mq.addEventListener('change', listener);
    setOsPrefers(mq.matches);
    return () => mq.removeEventListener('change', listener);
  }, []);
  if (storePref === 'always') return true;
  if (storePref === 'never') return false;
  return osPrefers;
}

export function useEscape(handler: (e: KeyboardEvent) => void, enabled = true): void {
  const handlerRef = useRef(handler);
  useEffect(() => { handlerRef.current = handler; }, [handler]);
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handlerRef.current(e);
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [enabled]);
}

export interface UseArrowNavOptions {
  orientation?: 'horizontal' | 'vertical' | 'both';
  onPrev?: (e: KeyboardEvent) => void;
  onNext?: (e: KeyboardEvent) => void;
  onHome?: (e: KeyboardEvent) => void;
  onEnd?: (e: KeyboardEvent) => void;
  enabled?: boolean;
}

export function useArrowNav(opts: UseArrowNavOptions): void {
  const { orientation = 'vertical', onPrev, onNext, onHome, onEnd, enabled = true } = opts;
  const ref = useRef(opts);
  useEffect(() => { ref.current = opts; }, [opts]);
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const listener = (e: KeyboardEvent) => {
      const horiz = orientation === 'horizontal' || orientation === 'both';
      const vert = orientation === 'vertical' || orientation === 'both';
      if (vert && e.key === 'ArrowUp')  { onPrev?.(e); }
      if (vert && e.key === 'ArrowDown') { onNext?.(e); }
      if (horiz && e.key === 'ArrowLeft') { onPrev?.(e); }
      if (horiz && e.key === 'ArrowRight') { onNext?.(e); }
      if (e.key === 'Home') onHome?.(e);
      if (e.key === 'End') onEnd?.(e);
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [enabled, orientation, onPrev, onNext, onHome, onEnd]);
}

export function useAriaDescribedBy(externalId?: string): {
  id: string;
  props: { 'aria-describedby': string };
} {
  const internal = useId();
  const id = externalId ?? internal;
  return { id, props: { 'aria-describedby': id } };
}

export function useContrastCheck(foreground: string, background: string): ContrastReport {
  return useMemo(() => reportContrast(foreground, background), [foreground, background]);
}

export function useStableId(prefix = 'rr'): string {
  const id = useId();
  return useMemo(() => `${prefix}-${id.replace(/[:]/g, '')}`, [id, prefix]);
}

export function useTextScale(): { textScale: TextScale; setTextScale: (s: TextScale) => void } {
  const textScale = useAppStore((s) => s.textScale);
  const setTextScale = useAppStore((s) => s.setTextScale);
  return { textScale, setTextScale };
}

export function useDensity(): { density: Density; setDensity: (d: Density) => void } {
  const density = useAppStore((s) => s.density);
  const setDensity = useAppStore((s) => s.setDensity);
  return { density, setDensity };
}

export function useHighContrast(): { highContrast: boolean; setHighContrast: (on: boolean) => void } {
  const highContrast = useAppStore((s) => s.highContrast);
  const setHighContrast = useAppStore((s) => s.setHighContrast);
  return { highContrast, setHighContrast };
}

export function useTypography(role: TypographyRole): TypographyBundle {
  const scale = useAppStore((s) => s.textScale);
  return useMemo(() => typography(role, { scale }), [role, scale]);
}

export function useSpacing(tier: SpacingTier, opts: SpacingRoleOptions = {}): number {
  const density = useAppStore((s) => s.density);
  return useMemo(() => Math.round(spacingSemantic[tier] * DENSITY_FACTORS[opts.density ?? density]), [tier, density, opts.density]);
}

export function useSpacingRole(use: SpacingUse): SpacingByUseBundle {
  const density = useAppStore((s) => s.density);
  return useMemo(() => spacingRole(use, { density }), [use, density]);
}

export function useTypographyVar(role: TypographyRole): Record<string, string | number> {
  const bundle = useTypography(role);
  return useMemo(
    () => ({
      '--rr-typography-font-family': bundle.fontFamily,
      '--rr-typography-font-size': `${bundle.fontSize}px`,
      '--rr-typography-font-weight': bundle.fontWeight,
      '--rr-typography-line-height': bundle.lineHeight,
      '--rr-typography-letter-spacing': bundle.letterSpacing,
      ...(bundle.textTransform ? { '--rr-typography-text-transform': bundle.textTransform } : {})
    }),
    [bundle]
  );
}
